import {
  DeviceGroupScheduleModel,
  WeekDayEnum
} from '@models/digital-signage/device-group-schedules.model.ts';
import {
  MinutesRange,
  ScheduleDateRange,
  ScheduleTiming
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import { WEEK_DAYS_API } from '@modules/digital-signage/utils/schedule.utils.ts';
import { toMinutes } from '@modules/digital-signage/utils/schedule-time.utils.ts';
import {
  getScheduleDates,
  sortDays
} from '@modules/digital-signage/utils/schedule-date.utils.ts';

export const getScheduleSlots = (
  schedule: Pick<DeviceGroupScheduleModel, 'schedules'>
) =>
  schedule.schedules.map((slot) => ({
    days: sortDays(
      slot.daysOfWeek.map((day) => WEEK_DAYS_API.indexOf(day) as WeekDayEnum)
    ),
    range: {
      start: toMinutes(slot.startTime),
      end: toMinutes(slot.endTime, true)
    }
  }));

export const toSlotKey = (day: WeekDayEnum, { start, end }: MinutesRange) =>
  `${day}|${start}|${end}`;

export const getSlotKeys = (
  schedule: Pick<DeviceGroupScheduleModel, 'schedules'>
): Set<string> =>
  new Set(
    getScheduleSlots(schedule).flatMap(({ days, range }) =>
      days.map((day) => toSlotKey(day, range))
    )
  );

export const getSavedSlotKeys = (
  saved: ScheduleTiming | undefined,
  period: ScheduleDateRange
): Set<string> => {
  if (!saved) return new Set();
  const savedPeriod = getScheduleDates(saved);
  if (savedPeriod.start !== period.start || savedPeriod.end !== period.end) {
    return new Set();
  }
  return getSlotKeys(saved);
};

export const isSavedRange = (
  days: WeekDayEnum[],
  range: MinutesRange,
  savedSlotKeys: Set<string>
): boolean =>
  days.length > 0 &&
  days.every((day) => savedSlotKeys.has(toSlotKey(day, range)));

export const isSavedStart = (
  days: WeekDayEnum[],
  start: number,
  savedSlotKeys: Set<string>
): boolean =>
  days.length > 0 &&
  days.every((day) =>
    [...savedSlotKeys].some((key) => key.startsWith(`${day}|${start}|`))
  );

export const getSavedDayRanges = (
  day: WeekDayEnum,
  ranges: MinutesRange[],
  savedSlotKeys: Set<string>
): MinutesRange[] =>
  ranges.filter((range) => savedSlotKeys.has(toSlotKey(day, range)));
